/**
 * Workspace Context
 * Phase 7: Workspace Surface Layer
 * 
 * Provides React context for workspace management including:
 * - Current page tracking
 * - Page navigation and actions
 * - Sidebar state management
 * - Content synchronization
 */

import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { eventBus } from '../event-bus';
import { savePages, loadPages, saveCurrentPageId, loadCurrentPageId, softDeletePage, restorePage, hardDeletePage, readTrashedPages } from './pageStorage';
import { saveDatabase, createDefaultDatabase } from './databaseStorage';
import { getSurfaceRouter } from '../router/SurfaceRouter';

/**
 * Page reference type
 */
export interface PageRef {
  id: string;
  title: string;
  parentId?: string;
  type?: 'page' | 'database' | 'view';
  /** Optional cover image — URL or base64 data URL. */
  cover?: string;
  /** Cover layout hint: 'wide' (16:5) or 'banner' (3:1) or 'none'. */
  coverLayout?: 'wide' | 'banner' | 'none';
  /** Cover position (vertical scroll) in 0–100, default 50 (center). */
  coverPosition?: number;
}

/**
 * Workspace Context Value Type
 */
export interface WorkspaceContextValue {
  // State
  currentPageId: string | null;
  currentPage: PageRef | null;
  sidebarCollapsed: boolean;
  pages: PageRef[];
  initialized: boolean;

  // Page actions
  selectPage: (pageId: string) => Promise<void>;
  goToHome: () => void;
  goBack: () => void;
  canGoBack: boolean;
  createPage: (title: string, parentId?: string) => Promise<PageRef>;
  updatePage: (pageId: string, updates: Partial<PageRef>) => Promise<void>;
  deletePage: (pageId: string) => Promise<void>;
  renamePage: (pageId: string, newName: string) => Promise<void>;
  movePage: (pageId: string, newParentId: string | null, beforeSiblingId?: string) => Promise<void>;

  // Sidebar actions
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;

  // F 350 — re-read pages from storage. The AI Assistant panel's
  // WorkspaceBridgeStorage calls this after every successful chat
  // create, so the sidebar reflects the change without a full reload.
  refreshPages: () => void;

  // Utility methods
  getPageById: (pageId: string) => PageRef | undefined;
  getChildPages: (parentId: string) => PageRef[];
  hasChildren: (pageId: string) => boolean;
}

/**
 * Workspace Context
 */
const WorkspaceContext = createContext<WorkspaceContextValue | undefined>(undefined);

/**
 * Workspace Provider Props
 */
export interface WorkspaceProviderProps {
  children: ReactNode;
}

/**
 * Default pages to create on first launch
 */
const DEFAULT_PAGES: PageRef[] = [
  {
    id: 'page-prd',
    title: 'Product Requirements Document',
    type: 'page'
  },
  {
    id: 'page-getting-started',
    title: 'Getting Started',
    type: 'page'
  },
  {
    id: 'page-daily-notes',
    title: 'Daily Notes',
    type: 'page'
  }
];

/**
 * Workspace Provider Component
 */
export const WorkspaceProvider: React.FC<WorkspaceProviderProps> = ({ children }) => {
  const [currentPageId, setCurrentPageId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<PageRef | null>(null);
  // Navigation history: stack of page IDs the user has visited, oldest at
  // the bottom. Used by the PageEditor's back button to return to the
  // previous page. Capped at 20 entries to prevent unbounded growth.
  const [pageHistory, setPageHistory] = useState<string[]>([]);
  const canGoBack = pageHistory.length > 1;
  const [sidebarCollapsed, setSidebarCollapsedState] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    // On mobile/tablet, default to CLOSED so the drawer doesn't cover
    // the content. On desktop, default to OPEN so the sidebar is visible.
    return window.innerWidth < 1024;
  });
  const [pages, setPages] = useState<PageRef[]>([]);
  const [initialized, setInitialized] = useState(false);

  /**
   * Initialize workspace with default pages
   */
  const initializeWorkspace = useCallback(async () => {
    try {
      // Try to load from storage first
      const storedPages = loadPages();
      
      if (storedPages.length > 0) {
        // Use stored pages
        setPages(storedPages);
        setCurrentPageId(storedPages[0].id);
        setCurrentPage(storedPages[0]);
        setInitialized(true);
        eventBus.emit('workspace:initialized', { pages: storedPages });
      } else {
        // Create default pages if no stored pages
        setPages(DEFAULT_PAGES);
        savePages(DEFAULT_PAGES); // Save defaults to storage
        // Auto-select the first page (PRD)
        setCurrentPageId(DEFAULT_PAGES[0].id);
        setCurrentPage(DEFAULT_PAGES[0]);
        setInitialized(true);
        eventBus.emit('workspace:initialized', { pages: DEFAULT_PAGES });
      }
    } catch (error) {
      console.error('[WorkspaceContext] Error initializing workspace:', error);
    }
  }, []);

  /**
   * Load pages from storage
   */
  const loadPagesFromStorage = useCallback(() => {
    try {
      const stored = loadPages();
      if (stored.length > 0) {
        setPages(stored);
        if (!currentPageId) {
          setCurrentPageId(stored[0].id);
          setCurrentPage(stored[0]);
        }
      }
      eventBus.emit('workspace:pages-loaded', { pages });
    } catch (error) {
      console.error('[WorkspaceContext] Error loading pages:', error);
    }
  }, [currentPageId]);

  /**
   * Initialize provider - load from localStorage or create defaults
   * Restore previously selected page if available
   */
  useEffect(() => {
    console.log('[WorkspaceContext] Initializing...');
    
    // Use setTimeout to ensure this runs after mount
    const timer = setTimeout(() => {
      try {
        const stored = loadPages();
        const savedCurrentPageId = loadCurrentPageId();
        console.log('[WorkspaceContext] Loaded pages:', stored.length, 'Saved page ID:', savedCurrentPageId);
        
        if (stored.length > 0) {
          // F 144 — a deep link is a selection, not a stale URL.
          //
          // The "no saved page -> home" branch below exists to clear a STALE
          // /page/:id from the address bar. That was harmless while every
          // browser started empty, but state now persists across browsers, so
          // a cold load of a perfectly valid deep link arrived here with no
          // saved selection and got cleared — the page existed, the user just
          // had never opened it on THIS device. Verified on live:
          // `deepLinkPath: "/"` for both page-prd and a runtime-created page.
          //
          // So: if the URL names a page that actually exists, honour it. Only
          // fall through to home when the URL is genuinely stale.
          // Promote the deep-linked id to the "saved" selection and let the
          // existing restore path below handle it, rather than duplicating
          // that block (which would duplicate its type debt too).
          //
          // The URL wins whenever the SAVED id does not resolve — not merely
          // when it is absent. A saved id can dangle: a page deleted on another
          // device leaves `currentPageId` pointing at nothing, and the old
          // `if (!savedCurrentPageId)` guard let that dangling id beat a valid
          // deep link, sending the user home. Hit this live — the id was left
          // behind by deleting a page elsewhere.
          const savedResolves = !!savedCurrentPageId && stored.some(p => p.id === savedCurrentPageId);
          let effectivePageId = savedResolves ? savedCurrentPageId! : null;
          if (!effectivePageId) {
            try {
              const m = window.location.pathname.match(/^\/page\/([^/]+)/);
              if (m && stored.some(p => p.id === m[1])) effectivePageId = m[1];
            } catch { /* no window (SSR) or malformed URL — fall through */ }
          }

          // Try to restore the previously selected page
          if (effectivePageId) {
            const page = stored.find(p => p.id === effectivePageId);
            if (page) {
              console.log('[WorkspaceContext] Restoring page:', page.title);
              setPages(stored);
              setCurrentPageId(page.id);
              setCurrentPage(page);
              if (!savedCurrentPageId) saveCurrentPageId(page.id);
              setInitialized(true);
              return;
            } else {
              console.log('[WorkspaceContext] Saved page not found, showing home');
              // F 145 B — a dangling id must not survive to the next load. It
              // would beat a valid deep link again, every time.
              saveCurrentPageId(null);
            }
          } else {
            console.log('[WorkspaceContext] No saved page, showing home');
          }
          setPages(stored);
          setCurrentPageId(null);
          setCurrentPage(null);
          setInitialized(true);
        } else {
          // Create default pages
          console.log('[WorkspaceContext] No stored pages, creating defaults');
          setPages(DEFAULT_PAGES);
          savePages(DEFAULT_PAGES);
          setCurrentPageId(null);
          setCurrentPage(null);
          saveCurrentPageId(null);
          setInitialized(true);
        }
        console.log('[WorkspaceContext] Initialization complete');
      } catch (error) {
        console.error('[WorkspaceContext] Initialization error:', error);
        setPages(DEFAULT_PAGES);
        setCurrentPageId(null);
        setCurrentPage(null);
        setInitialized(true);
      }
    }, 0);
    
    return () => clearTimeout(timer);
  }, []);

  /**
   * Setup event listeners
   */
  useEffect(() => {
    const handlePageUpdated = (data: any) => {
      if (data.pages) {
        setPages(data.pages);
      }
    };

    const handlePageSelected = (data: any) => {
      if (data.pageId) {
        setCurrentPageId(data.pageId);
        const page = pages.find(p => p.id === data.pageId);
        if (page) {
          setCurrentPage(page);
        }
      }
    };

    eventBus.on('pages-loaded', handlePageUpdated);
    eventBus.on('page-selected', handlePageSelected);

    return () => {
      eventBus.off('pages-loaded', handlePageUpdated);
      eventBus.off('page-selected', handlePageSelected);
    };
  }, [pages]);

  /**
   * Select a page
   */
  const selectPage = useCallback(async (pageId: string) => {
    try {
      const page = pages.find(p => p.id === pageId);
      if (page) {
        console.log('[WorkspaceContext] selectPage:', page.title);
        setCurrentPageId(pageId);
        setCurrentPage(page);
        // Push to history (capped at 20). Skip if same as last entry
        // to avoid duplicates when the user re-selects the same page.
        setPageHistory(prev => {
          if (prev[prev.length - 1] === pageId) return prev;
          const next = [...prev, pageId];
          return next.length > 20 ? next.slice(next.length - 20) : next;
        });
        saveCurrentPageId(pageId); // Persist the selection
        eventBus.emit('page-selected', { pageId, page });
      }
    } catch (error) {
      console.error('[WorkspaceContext] Error selecting page:', error);
      throw error;
    }
  }, [pages]);

  /**
   * Go back to home/gallery view - deselect current page
   */
  const goToHome = useCallback(() => {
    console.log('[WorkspaceContext] goToHome called');
    setCurrentPageId(null);
    setCurrentPage(null);
    saveCurrentPageId(null); // Clear saved selection
    eventBus.emit('page-selected', { pageId: null, page: null });
    // Clear the URL too — otherwise a stale /page/:id in the address bar
    // would be re-hydrated on refresh and override the "home" state.
    try {
      getSurfaceRouter().updateBrowserUrl('/', true);
    } catch (error) {
      console.error('[WorkspaceContext] Failed to clear URL on goToHome:', error);
    }
  }, []);

  /**
   * Go back to the previous page in history. Pops the current page off
   * the history stack and selects the one before it. If the stack is
   * empty (only one entry), falls back to goToHome().
   */
  const goBack = useCallback(() => {
    setPageHistory(prev => {
      if (prev.length <= 1) {
        // No previous page — go home
        goToHome();
        return prev;
      }
      const next = prev.slice(0, -1);
      const targetId = next[next.length - 1];
      const target = pages.find(p => p.id === targetId);
      if (target) {
        setCurrentPageId(targetId);
        setCurrentPage(target);
        saveCurrentPageId(targetId);
        eventBus.emit('page-selected', { pageId: targetId, page: target });
        try { getSurfaceRouter().updateBrowserUrl(`/page/${targetId}`, true); } catch {}
      }
      return next;
    });
  }, [pages, goToHome]);

  /**
   * Create a new page
   * @param title - The page title
   * @param parentId - Optional parent page id
   * @param type - Optional page type. 'database' creates a database-typed page with a default database.
   */
  const createPage = useCallback(async (title: string, parentId?: string, type: 'page' | 'database' = 'page'): Promise<PageRef> => {
    try {
      const newPage: PageRef = {
        id: `page-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        title,
        parentId,
        type
      };

      setPages(prev => {
        const updated = [...prev, newPage];
        savePages(updated); // Persist to storage
        return updated;
      });
      // If this is a database page, also create the database with default schema.
      // The page id doubles as the database id so lookups are direct.
      if (type === 'database') {
        const db = createDefaultDatabase(newPage.id, title);
        saveDatabase(db);
      }
      // Auto-select the newly created page
      setCurrentPageId(newPage.id);
      setCurrentPage(newPage);
      saveCurrentPageId(newPage.id); // Persist the selection
      eventBus.emit('page-created', { page: newPage });

      return newPage;
    } catch (error) {
      console.error('[WorkspaceContext] Error creating page:', error);
      throw error;
    }
  }, []);

  /**
   * Update a page
   */
  const updatePage = useCallback(async (pageId: string, updates: Partial<PageRef>) => {
    try {
      setPages(prev => {
        const updated = prev.map(p => p.id === pageId ? { ...p, ...updates } : p);
        savePages(updated); // Persist to storage
        return updated;
      });

      if (currentPageId === pageId) {
        setCurrentPage(prev => prev ? { ...prev, ...updates } : null);
      }

      eventBus.emit('page-updated', { pageId, updates });
    } catch (error) {
      console.error('[WorkspaceContext] Error updating page:', error);
      throw error;
    }
  }, [currentPageId]);

  /**
   * Delete a page
   */
  /**
   * F 226 — TRASH. This is now a SOFT delete.
   *
   * The row is kept and stamped with `deletedAt`; `loadPages` filters it
   * out of every default read, and the Trash view is the only thing that
   * sets `includeDeleted`. The visible result is identical to the old
   * hard delete — the page disappears from the sidebar — but it is
   * recoverable, which is the whole point of the feature.
   *
   * `hardDeletePage` below is the operator path and is what the Trash
   * view's "Delete permanently" calls.
   */
  const deletePage = useCallback(async (pageId: string) => {
    try {
      softDeletePage(pageId);
      setPages(prev => {
        const updated = prev.filter(p => p.id !== pageId);
        return updated;
      });

      if (currentPageId === pageId) {
        setCurrentPageId(null);
        setCurrentPage(null);
      }

      eventBus.emit('page-deleted', { pageId });
    } catch (error) {
      console.error('[WorkspaceContext] Error deleting page:', error);
      throw error;
    }
  }, [currentPageId]);

  /**
   * Rename a page
   */
  const renamePage = useCallback(async (pageId: string, newName: string) => {
    try {
      await updatePage(pageId, { title: newName });
      eventBus.emit('page-renamed', { pageId, newName });
    } catch (error) {
      console.error('[WorkspaceContext] Error renaming page:', error);
      throw error;
    }
  }, [updatePage]);

  /**
   * Move a page to a new parent / sibling position. Cycle-safe: refuses to
   * reparent a node into itself or any of its descendants. Reorders by
   * splicing the moved node out and back in at `beforeSiblingId` (or end).
   */
  const movePage = useCallback(async (
    pageId: string,
    newParentId: string | null,
    beforeSiblingId?: string,
  ) => {
    try {
      setPages(prev => {
        const dragged = prev.find(p => p.id === pageId);
        if (!dragged) return prev;
        // Cycle prevention: walk descendants of newParentId; refuse if we
        // would land inside our own subtree.
        const isOwnDescendant = (pid: string | null, candidateId: string): boolean => {
          let cur: string | undefined = pid ?? undefined;
          const seen = new Set<string>();
          while (cur && !seen.has(cur)) {
            seen.add(cur);
            if (cur === candidateId) return true;
            cur = prev.find(p => p.id === cur)?.parentId;
          }
          return false;
        };
        if (newParentId && (newParentId === pageId || isOwnDescendant(newParentId, pageId))) {
          console.warn('[WorkspaceContext] movePage refused: cycle');
          return prev;
        }
        const filtered = prev.filter(p => p.id !== pageId);
        const moved: PageRef = { ...dragged, parentId: newParentId ?? undefined };
        const insertAt = (() => {
          if (!beforeSiblingId) return filtered.length;
          const idx = filtered.findIndex(p => p.id === beforeSiblingId);
          return idx >= 0 ? idx : filtered.length;
        })();
        const next = [...filtered.slice(0, insertAt), moved, ...filtered.slice(insertAt)];
        savePages(next);
        return next;
      });
      eventBus.emit('page-moved', { pageId, newParentId, beforeSiblingId });
    } catch (error) {
      console.error('[WorkspaceContext] Error moving page:', error);
      throw error;
    }
  }, []);

  /**
   * Toggle sidebar collapsed state
   */
  const toggleSidebar = useCallback(() => {
    setSidebarCollapsedState(prev => !prev);
  }, []);

  /**
   * Set sidebar collapsed state
   */
  const setSidebarCollapsed = useCallback((collapsed: boolean) => {
    setSidebarCollapsedState(collapsed);
  }, []);

  /**
   * Get page by ID
   */
  const getPageById = useCallback((pageId: string) => {
    return pages.find(p => p.id === pageId);
  }, [pages]);

  /**
   * Get child pages
   */
  const getChildPages = useCallback((parentId: string) => {
    return pages.filter(p => p.parentId === parentId);
  }, [pages]);

  /**
   * F 350 — re-read pages from storage on demand. The AI Assistant
   * panel's bridge adapter calls this after every successful chat
   * create so the sidebar reflects the change.
   */
  const refreshPages = useCallback(() => {
    loadPagesFromStorage();
  }, [loadPagesFromStorage]);

  /**
   * Check if page has children
   */
  const hasChildren = useCallback((pageId: string) => {
    return pages.some(p => p.parentId === pageId);
  }, [pages]);

  const value: WorkspaceContextValue = {
    // State
    currentPageId,
    currentPage,
    sidebarCollapsed,
    pages,
    initialized,

    // Page actions
    selectPage,
    goToHome,
    goBack,
    canGoBack,
    createPage,
    updatePage,
    deletePage,
    renamePage,
    movePage,

    // Sidebar actions
    toggleSidebar,
    setSidebarCollapsed,

    // F 350 — see note on the interface. Used by the AI Assistant
    // panel's WorkspaceBridgeStorage.
    refreshPages,

    // Utility methods
    getPageById,
    getChildPages,
    hasChildren
  };

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
};

/**
 * useWorkspace Hook
 * 
 * Use this hook to access workspace management functionality
 * Must be used within a WorkspaceProvider
 */
export function useWorkspace(): WorkspaceContextValue {
  const context = useContext(WorkspaceContext);

  if (!context) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider');
  }

  return context;
}

/**
 * useCurrentPage Hook
 * 
 * Returns only the current page and related actions
 */
export function useCurrentPage() {
  const context = useWorkspace();
  return {
    currentPageId: context.currentPageId,
    currentPage: context.currentPage,
    selectPage: context.selectPage,
    renamePage: context.renamePage
  };
}

/**
 * usePageActions Hook
 * 
 * Returns page manipulation methods
 */
export function usePageActions() {
  const context = useWorkspace();
  return {
    createPage: context.createPage,
    updatePage: context.updatePage,
    deletePage: context.deletePage,
    renamePage: context.renamePage
  };
}

/**
 * useSidebarState Hook
 * 
 * Returns sidebar state and actions
 */
export function useSidebarState() {
  const context = useWorkspace();
  return {
    collapsed: context.sidebarCollapsed,
    toggle: context.toggleSidebar,
    setCollapsed: context.setSidebarCollapsed
  };
}

export type { WorkspaceContextValue, PageRef };
